/**
 * Issue #232 — 最短有效质检时长（KPI考核）纯逻辑 + 文案常量。
 *
 * admin vitest 无路由 / 组件渲染夹具（UI 行为由 Playwright e2e 覆盖），因此把
 * 「能否保存 / 区间校验 / 钳制 / 文案」抽成纯函数：QcMinDurationSettingsCard 仅做
 * 薄包装并复用本模块，关键决策（尤其是「只读角色绝不触发保存」）由单测锁定，
 * 杜绝纯前端假状态、保证卡片不会再次消失。区间 / 默认值以后端下发为准（10-600，默认 60）。
 */
export const QC_MIN_DURATION_LABEL = '最短有效质检时长';

/** 兜底默认（仅在后端尚未下发时占位；正式取值始终以接口返回为准）。 */
export const QC_MIN_DURATION_DEFAULT_SEC = 60;
export const QC_MIN_DURATION_MIN_SEC = 10;
export const QC_MIN_DURATION_MAX_SEC = 600;

export interface QcMinDurationRange {
  minDurationSec: number;
  maxDurationSec: number;
  defaultDurationSec: number;
}

/** 接口缺失时使用的兜底区间（与后端常量一致）。 */
export const DEFAULT_QC_MIN_DURATION_RANGE: QcMinDurationRange = {
  minDurationSec: QC_MIN_DURATION_MIN_SEC,
  maxDurationSec: QC_MIN_DURATION_MAX_SEC,
  defaultDurationSec: QC_MIN_DURATION_DEFAULT_SEC,
};

/** 草稿字符串 → 是否为合法整数秒且落在区间内（非数字 / 小数 / 越界一律判否）。 */
export function isQcMinDurationInRange(raw: string, range: QcMinDurationRange): boolean {
  const trimmed = raw.trim();
  if (trimmed === '') return false;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return false;
  return n >= range.minDurationSec && n <= range.maxDurationSec;
}

/** 把任意输入钳制到合法区间整数（与后端 clampQcMinValidDuration 同语义，用于前端展示兜底）。 */
export function clampQcMinDuration(value: number, range: QcMinDurationRange): number {
  if (!Number.isFinite(value)) return range.defaultDurationSec;
  const intVal = Math.round(value);
  if (intVal < range.minDurationSec) return range.minDurationSec;
  if (intVal > range.maxDurationSec) return range.maxDurationSec;
  return intVal;
}

/**
 * 能否保存：必须可编辑（ADMIN/SUPER_ADMIN）、当前未在保存中、草稿在合法区间。
 * `canEdit=false`（只读 / 非编辑角色）永远返回 false —— 卡片据此禁用输入并隐藏保存按钮，
 * 任何只读路径都不会触发真实 PATCH。
 */
export function canSaveQcMinDuration(args: {
  canEdit: boolean;
  pending: boolean;
  raw: string;
  range: QcMinDurationRange;
}): boolean {
  if (!args.canEdit) return false;
  if (args.pending) return false;
  return isQcMinDurationInRange(args.raw, args.range);
}

/** 合法区间的中文提示文案（展示当前允许范围与默认值）。 */
export function qcMinDurationRangeHint(range: QcMinDurationRange): string {
  return `合法区间 ${range.minDurationSec}-${range.maxDurationSec} 秒，默认 ${range.defaultDurationSec} 秒`;
}
